// apps/web/src/features/russia-requests/components/RussiaRequestDrawer.tsx
//
// Drawer over `/russia-rates/:id`. The lean drawer (#1548) surfaces
// exactly three role-aware sections — cargo / cost / chat — plus the #944
// action surface: sticky transition bar at the drawer bottom, RETURN
// banner above the tab panel, and a friendly 404 / 403 stub when the row
// is unavailable.
//
// @aiPurpose URL is the canonical source: `:id` drives the open state,
//   `?tab=` drives the active tab (#940). Close handlers navigate back
//   to `/russia-rates`. Drawer is rendered as a sibling overlay on the
//   list page so backgrounding the modal does not unmount the list
//   cache.

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router';
import {
  RussiaRequestRefusalReason,
  isDraftLikeRequestStatus,
  russiaSendMissing,
  ErrorCode,
  type RussiaRequest,
} from '@magimp/shared';

import { type RussiaTransitionBody } from '../../../api/russia-requests/index.js';
import { ApiClientError, errorMessage } from '../../../api/errors.js';
import { EditorDrawer, useNarrow } from '../../../ui/index.js';
import { hasPermission, useAuthStore } from '../../../stores/auth.js';
import { useToastStore } from '../../../stores/toast.js';
import {
  useDuplicateRussiaRequest,
  useRussiaRequest,
  useTransitionRussiaRequest,
} from '../queries.js';
import { useRussiaRequestUrlState } from '../useRussiaRequestUrlState.js';
import { useAutoMarkNotificationsViewed } from '../../notifications/index.js';
import {
  allowedRussiaActions,
  canActAsRussiaRequestChatParty,
  canDuplicateRussiaRequest,
  canSeeChinaParentFromRussia,
  type RussiaTransitionAction,
} from '../role.js';
import { RussiaRequestDrawerHeader } from './RussiaRequestDrawerHeader.js';
import { RussiaRequestDrawerLoadingShell } from './RussiaRequestDrawerLoadingShell.js';
import { RussiaRequestDrawerReturnBanner } from './RussiaRequestDrawerReturnBanner.js';
import { RussiaRequestDrawerSendReadinessHint } from './RussiaRequestDrawerSendReadinessHint.js';
import { RussiaRequestDrawerStickyActionBar } from './RussiaRequestDrawerStickyActionBar.js';
import { RussiaRequestDrawerTabs, type RussiaDrawerTabId } from './RussiaRequestDrawerTabs.js';
import {
  RussiaTransitionConfirmDialog,
  RussiaTransitionReasonDialog,
} from './RussiaRequestDrawerTransitionDialogs.js';
import { RussiaRequestDrawerUnavailableStub } from './RussiaRequestDrawerUnavailableStub.js';
import { CargoTab } from './tabs/CargoTab.js';
import { ChatTab } from './tabs/ChatTab.js';
import { CostTab } from './tabs/CostTab.js';
import { detectRoleKind, type UserRef } from '../../requests/display/index.js';
import { missingToMessageKey, narrowMissing } from './russiaDrawerSendReadiness.js';
import type { DrawerTabKind } from '../../notifications/index.js';

export function RussiaRequestDrawer() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  // INVARIANT (#944): the error branch closes with `replace: true` so
  //   the broken `:id` does not leak into browser history; a normal
  //   close keeps the entry so the back-button returns to the previous
  //   row.
  const close = () => navigate('/russia-rates', { replace: false });
  const closeUnavailable = () => navigate('/russia-rates', { replace: true });

  if (!id) return null;

  return (
    <RussiaRequestDrawerInner
      requestId={id}
      onClose={close}
      onCloseUnavailable={closeUnavailable}
    />
  );
}

type PendingModal =
  | { kind: 'confirm'; action: RussiaTransitionAction }
  | { kind: 'reason'; action: RussiaTransitionAction }
  | null;

function RussiaRequestDrawerInner({
  requestId,
  onClose,
  onCloseUnavailable,
}: {
  requestId: string;
  onClose: () => void;
  onCloseUnavailable: () => void;
}) {
  const { t } = useTranslation(['russiaRequests', 'common']);
  const query = useRussiaRequest(requestId);
  const { currentTab, switchTab } = useRussiaRequestUrlState();
  // INVARIANT (#972): narrow viewports collapse the drawer to full-screen
  //   geometry and swap inline header transitions for the bottom sticky
  //   action bar — the same rhythm as the China drawer. The shared
  //   threshold (`useNarrow` defaults to 900px) keeps the breakpoint
  //   identical to the list page so the drawer never opens at desktop
  //   width on a viewport that is rendering mobile cards.
  const narrow = useNarrow();

  // INVARIANT (#944): on the error branch, Escape / backdrop / drawer-close
  //   must replace the broken `:id` in history rather than push, so the
  //   404 / 403 id does not leak into back-button state. Route the
  //   drawer-level `onConfirmClose` through `onCloseUnavailable` whenever
  //   the detail query is in the error state.
  const confirmClose = query.isError ? onCloseUnavailable : onClose;

  return (
    <EditorDrawer dirty={false} onConfirmClose={confirmClose} width={880} narrow={narrow}>
      <div
        style={{ display: 'flex', flexDirection: 'column', height: '100%', minWidth: 0 }}
        data-testid="russia-request-drawer"
      >
        {query.isError ? (
          <RussiaRequestDrawerUnavailableStub requestId={requestId} onClose={onCloseUnavailable} />
        ) : query.isLoading ? (
          <>
            <RussiaRequestDrawerLoadingShell id={requestId} onClose={onClose} />
            <p style={{ padding: 18 }} data-testid="russia-request-drawer-loading">
              {t('russiaRequests:loading')}
            </p>
          </>
        ) : !query.data ? null : (
          <RussiaRequestDrawerBody
            request={query.data}
            onClose={onClose}
            currentTab={currentTab}
            switchTab={switchTab}
            narrow={narrow}
          />
        )}
      </div>
    </EditorDrawer>
  );
}

function RussiaRequestDrawerBody({
  request,
  onClose,
  currentTab,
  switchTab,
  narrow,
}: {
  request: RussiaRequest;
  onClose: () => void;
  currentTab: RussiaDrawerTabId;
  switchTab: (id: RussiaDrawerTabId) => void;
  narrow: boolean;
}) {
  const { t } = useTranslation('russiaRequests');
  const navigate = useNavigate();
  const permissions = useAuthStore((s) => s.permissions);
  const user = useAuthStore((s) => s.user);
  const showToast = useToastStore((s) => s.show);
  const transitionMutation = useTransitionRussiaRequest(request.id);
  const duplicateMutation = useDuplicateRussiaRequest();
  const [modal, setModal] = useState<PendingModal>(null);

  // WHY (#1571): the complete-draft edit affordance opens the draft editor so a
  //   Manager can complete an incomplete DRAFT/RETURN draft (COMPLEX totals or
  //   SIMPLE positions) and then send it. Gated on the same write permission
  //   the backend requires to PATCH the row (`russiaRequests.manage`) AND a
  //   draft-like status — hidden for IN_WORK and every later status.
  const canEdit =
    isDraftLikeRequestStatus(request.status) && hasPermission(permissions, 'russiaRequests.manage');

  // WHY (#1572): proactive readiness hint — reuse the shared predicate so the
  //   client-side check mirrors the backend SEND gate exactly. Only computed
  //   for draft-like statuses (DRAFT/RETURN); empty array otherwise so the
  //   hint component renders nothing.
  const sendMissing = isDraftLikeRequestStatus(request.status)
    ? russiaSendMissing({
        kind: request.kind,
        items: request.items,
        totalWeight: request.totalWeight,
        totalVolume: request.totalVolume,
        endAddress: request.endAddress,
      })
    : [];

  // WHY (#1067): chat tab needs an actor identity for own-vs-other styling.
  //   Falls back to a synthetic guest ref when the store is empty (e.g.
  //   during sign-out unmount) so the renderer keeps its prop contract.
  const me: UserRef = user
    ? { id: user.id, name: user.name, role: detectRoleKind(user) }
    : { id: '__none__', name: '', role: 'manager' };

  const actions = allowedRussiaActions(request.status, permissions);
  // WHY (#1064): UI defense-in-depth for the China-parent affordance.
  //   #1063 already nulls `parentRequestId` on the wire for logist_ru,
  //   but the issue mandates this UI gate so a future backend regression
  //   cannot re-leak the link into the header chip.
  const canSeeChinaParent = canSeeChinaParentFromRussia(permissions);
  // INVARIANT (#1067): UI defense-in-depth for the RF chat tab. Mirrors
  //   the backend `isChatParty` rule so a row-visible-but-non-party
  //   actor (e.g. an unassigned logist_ru) never sees the chat tab, the
  //   unread-summary chip, or the composer/thread. Backend still rejects
  //   non-party calls with 404, but the UI guarantees the affordance
  //   never mounts.
  const canSeeChat = canActAsRussiaRequestChatParty(permissions, user?.id ?? null, {
    managerId: request.managerId,
    logistId: request.logistId,
  });
  // INVARIANT (#1067/#1548): a non-party landing on `?tab=chat` falls back
  //   to the cargo panel. Resolve the effective tab here so the tab
  //   strip's active button, the tabpanel `id`, and `aria-labelledby` all
  //   converge on `cargo` instead of pointing at a filtered-out `chat`
  //   button. The URL state intentionally stays untouched — a follow-up
  //   redirect would compete with route writes.
  const effectiveTab: RussiaDrawerTabId =
    currentTab === 'chat' && !canSeeChat ? 'cargo' : currentTab;

  // WHY (#1548): the shared `useAutoMarkNotificationsViewed` hook keys on
  //   the legacy routing-tab kinds (`info`/`carriers`/`chat`). Map the
  //   lean drawer's section onto those so cargo marks the info-side
  //   notifications and cost marks the carrier-side ones, preserving the
  //   #1072 auto-view behavior.
  const notificationTab: DrawerTabKind =
    effectiveTab === 'cost' ? 'carriers' : effectiveTab === 'chat' ? 'chat' : 'info';

  // INVARIANT (#1072 Codex round-2 F-002): drive auto-view from the
  //   `effectiveTab`, not the raw `currentTab`. A non-party landing on
  //   `?tab=chat` collapses to `cargo` in the body but `currentTab`
  //   still reads `'chat'`; passing it would mark RF chat notifications
  //   viewed for content the user is not actually viewing.
  useAutoMarkNotificationsViewed({
    entityType: 'RUSSIA_REQUEST',
    entityId: request.id,
    currentTab: notificationTab,
  });

  const pending = transitionMutation.isPending;

  const fire = (action: RussiaTransitionAction, reason?: string) => {
    // INVARIANT (#944): guard against re-entry while a transition is
    //   in-flight. The sticky bar already disables its buttons but the
    //   modal confirm / submit paths re-enter `fire` directly, and double
    //   submits would otherwise POST twice before the mutation settles.
    if (pending) return;
    // INVARIANT (tsconfig.exactOptionalPropertyTypes): build the mutation
    //   variables object so `body` is omitted entirely when no body is
    //   needed, instead of set to undefined — `useTransitionRussiaRequest`
    //   types `body` as `?:` not `: T | undefined`.
    // WHY (#1544): `refuse` carries a structured `RussiaRequestRefusalReason`.
    //   The minimal drawer fires it with `OTHER` (the reason / comment
    //   picker is #1548); `success` carries no body (the winner is the
    //   stored `winnerBidId`, picked via the minimal winner control on the
    //   carriers tab — #1548 rebuilds the full cost section); `return` /
    //   `reopen` carry the freeform `reason` string from the reason modal.
    let variables: { key: RussiaTransitionAction['key']; body?: RussiaTransitionBody };
    if (action.key === 'refuse') {
      variables = { key: action.key, body: { reason: RussiaRequestRefusalReason.OTHER } };
    } else if (reason !== undefined) {
      variables = { key: action.key, body: { reason } };
    } else {
      variables = { key: action.key };
    }
    transitionMutation.mutate(variables, {
      onError: (err) => {
        // WHY (#1572): surface precise send-readiness copy when the
        //   backend rejects with a DRAFT_INCOMPLETE gate failure, so the
        //   Manager knows exactly what to fill in. For other transition
        //   errors (INVALID_TRANSITION, FORBIDDEN, etc.) use the
        //   centralized errorMessage() which maps error codes to locale
        //   keys. The generic fallback only fires when neither path
        //   matches (e.g. an unknown code not yet in MESSAGE_KEYS).
        if (
          err instanceof ApiClientError &&
          err.code === ErrorCode.GATE_FAILED &&
          err.meta?.['gate'] === 'DRAFT_INCOMPLETE'
        ) {
          const missing = narrowMissing(err.meta['missing']);
          if (missing.length > 0) {
            showToast({ tone: 'error', text: t(missingToMessageKey(missing)) });
            return;
          }
        }
        const specific = errorMessage(err);
        showToast({
          tone: 'error',
          text: specific !== '' ? specific : t('drawer.transitionError'),
        });
      },
    });
  };

  const onAction = (a: RussiaTransitionAction) => {
    if (pending) return;
    if (a.needsReason) {
      setModal({ kind: 'reason', action: a });
      return;
    }
    if (a.needsConfirm) {
      setModal({ kind: 'confirm', action: a });
      return;
    }
    fire(a);
  };

  // #2120: owner-only (or admin) duplicate affordance, any source status.
  //   Single-flight: the button is disabled while the mutation is in flight,
  //   so a double-click cannot mint two rows (v1 has no server idempotency).
  //   On success the drawer navigates to the new DRAFT at /russia-rates/:id.
  const duplicateVisible = canDuplicateRussiaRequest(permissions, user?.id ?? null, {
    managerId: request.managerId,
  });
  const onDuplicateClick = () => {
    if (duplicateMutation.isPending) return;
    duplicateMutation.mutate(
      { id: request.id },
      {
        onSuccess: (row) => navigate(`/russia-rates/${encodeURIComponent(row.id)}`),
        onError: (err) => {
          const specific = errorMessage(err);
          showToast({
            tone: 'error',
            text: specific !== '' ? specific : t('drawer.transitionError'),
          });
        },
      },
    );
  };

  return (
    <>
      <RussiaRequestDrawerHeader
        request={request}
        onClose={onClose}
        narrow={narrow}
        actions={actions}
        onAction={onAction}
        pending={pending}
        duplicate={{
          visible: duplicateVisible,
          pending: duplicateMutation.isPending,
          onClick: onDuplicateClick,
        }}
        canSeeChinaParent={canSeeChinaParent}
        canEdit={canEdit}
        onEdit={() => navigate(`/russia-rates/${encodeURIComponent(request.id)}/edit`)}
      />
      <RussiaRequestDrawerTabs
        request={request}
        tab={effectiveTab}
        setTab={switchTab}
        narrow={narrow}
        canSeeChat={canSeeChat}
      />
      <RussiaRequestDrawerReturnBanner request={request} />
      <RussiaRequestDrawerSendReadinessHint missing={sendMissing} />
      <div
        role="tabpanel"
        id={`russia-request-drawer-tabpanel-${effectiveTab}`}
        aria-labelledby={`russia-request-drawer-tab-${effectiveTab}`}
        data-testid={`russia-request-drawer-tabpanel-${effectiveTab}`}
        style={{ flex: 1, overflow: 'auto', padding: 18, minHeight: 0 }}
      >
        {effectiveTab === 'cargo' ? (
          <CargoTab request={request} />
        ) : effectiveTab === 'cost' ? (
          <CostTab request={request} />
        ) : (
          // INVARIANT (#1067): `effectiveTab` collapses `?tab=chat` to
          //   `cargo` above when the actor is not a chat party, so
          //   reaching this branch implies `canSeeChat === true` and the
          //   chat panel only mounts for parties.
          <ChatTab requestId={request.id} me={me} />
        )}
      </div>
      {/* INVARIANT (#972): the sticky action bar mirrors the China rhythm —
         desktop drives transitions from the header, narrow swaps in the
         bottom sticky bar so the close affordance stays reachable at the
         top of the viewport without scrolling. */}
      {narrow && (
        <RussiaRequestDrawerStickyActionBar
          actions={actions}
          onAction={onAction}
          pending={pending}
        />
      )}
      {modal?.kind === 'confirm' && (
        <RussiaTransitionConfirmDialog
          action={modal.action}
          fire={fire}
          onClose={() => setModal(null)}
        />
      )}
      {modal?.kind === 'reason' && (
        <RussiaTransitionReasonDialog
          action={modal.action}
          fire={fire}
          onClose={() => setModal(null)}
        />
      )}
    </>
  );
}
